import {
  AssetAttributionCapitalInvestmentCreateDocument,
  AssetAttributionCapitalInvestmentDeleteDocument,
  AssetAttributionCapitalInvestmentListByProjectProfileDocument,
  AssetAttributionCapitalInvestmentUpdateDocument,
  AssetAttributionCommitmentCreateDocument,
  AssetAttributionCommitmentDeleteDocument,
  AssetAttributionCommitmentListByProjectProfileDocument,
  AssetAttributionCommitmentUpdateDocument,
  AssetAttributionExitCreateDocument,
  AssetAttributionExitDeleteDocument,
  AssetAttributionExitSummaryByProjectProfileDocument,
  AssetAttributionExitUpdateDocument,
  AssetAttributionInvestmentCreateDocument,
  AssetAttributionInvestmentDeleteDocument,
  AssetAttributionInvestmentListByProjectProfileDocument,
  AssetAttributionInvestmentUpdateDocument,
  AssetAttributionListAssetAttributionDescriptionsDocument,
  AssetAttributionListCapitalInvestmentTypesDocument,
  AssetAttributionListInvestmentChecksDocument,
  AssetAttributionListInvestmentCurrenciesDocument,
  AssetAttributionListInvestmentRoundsDocument,
  AssetAttributionPaginatedDocument,
  AssetAttributionReceivedTokenCreateDocument,
  AssetAttributionReceivedTokenDeleteDocument,
  AssetAttributionReceivedTokenUpdateDocument,
  AssetAttributionUpdateDocument,
  AssetAttributionVestingScheduleUpsertDocument,
  VentureAssetAttributionGetByProjectProfileIdDocument,
  VentureAssetAttributionListExitTypeDocument,
  VentureAssetAttributionListInvestmentSourceDocument,
  VentureAssetAttributionReceivedTokenListByProjectProfileDocument,
  VentureAssetAttributionVestingScheduleListByProjectProfileDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { InvestmentTypeEnum, PartialNullable } from "config";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";
import {
  AssetAttributionCreateUpdateResponse,
  IAssetAttributionCapitalInvestmentCreateVars,
  IAssetAttributionCapitalInvestmentDeleteVars,
  IAssetAttributionCapitalInvestmentModel,
  IAssetAttributionCapitalInvestmentUpdateVars,
  IAssetAttributionCommitmentCreateVars,
  IAssetAttributionCommitmentDeleteVars,
  IAssetAttributionCommitmentModel,
  IAssetAttributionCommitmentUpdateVars,
  IAssetAttributionDescription,
  IAssetAttributionExitCreateVars,
  IAssetAttributionExitDeleteVars,
  IAssetAttributionExitSummaryModel,
  IAssetAttributionExitUpdateVars,
  IAssetAttributionInvestmentCreateVars,
  IAssetAttributionInvestmentDeleteVars,
  IAssetAttributionInvestmentModel,
  IAssetAttributionInvestmentUpdateVars,
  IAssetAttributionModel,
  IAssetAttributionPaginatedResponse,
  IAssetAttributionReceivedTokenCreateVars,
  IAssetAttributionReceivedTokenModel,
  IAssetAttributionReceivedTokenUpdateVars,
  IAssetAttributionsPaginatedVars,
  IAssetAttributionUpdateVars,
  IAssetAttributionVestingScheduleModel,
  IAssetAttributionVestingScheduleUpsertVars,
  ICapitalInvestmentType,
  IInvestmentCheck,
  IInvestmentRound,
} from "./types";
import { IInvestmentCurrency } from "../project-profile";

export async function getAssetAttributionsPaginated(
  variables: IAssetAttributionsPaginatedVars
): Promise<IAssetAttributionPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureAssetAttributionPaginated;
  } catch (error) {
    throw error;
  }
}

// export async function createAssetAttribution(
//   variables: IAssetAttributionCreateVars
// ): Promise<AssetAttributionCreateUpdateResponse> {
//   try {
//     const makeRequest = getRequestInstance(RequestTypes.MUTATE);
//     const res = await makeRequest({
//       mutation: AssetAttributionCreateDocument,
//       variables,
//     });
//     return res?.data?.ventureAssetAttributionCreate;
//   } catch (error) {
//     throw error;
//   }
// }

export async function updateAssetAttribution(
  variables: IAssetAttributionUpdateVars
): Promise<AssetAttributionCreateUpdateResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionPaginated",
        "ventureAssetAttributionGetByProjectProfileId",
      ],
    });

    return res?.data?.ventureAssetAttributionUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getAssetAttributionByProject(
  projectProfileId: number
): Promise<PartialNullable<IAssetAttributionModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureAssetAttributionGetByProjectProfileIdDocument,
      variables: {
        projectProfileId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureAssetAttributionGetByProjectProfileId;
  } catch (error) {
    throw error;
  }
}

// export async function deleteAssetAttribution(
//   assetAttributionId: number
// ): Promise<boolean> {
//   try {
//     const makeRequest = getRequestInstance(RequestTypes.MUTATE);
//     const res = await makeRequest({
//       mutation: AssetAttributionDeleteDocument,
//       variables: {
//         assetAttributionId,
//       },
//     });
//     return Boolean(res?.data?.ventureAssetAttributionDelete);
//   } catch (error) {
//     throw error;
//   }
// }

export async function listInvestmentSourceForAssetAttribution(
  investmentType: InvestmentTypeEnum,
  projectProfileId?: number
): Promise<string[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureAssetAttributionListInvestmentSourceDocument,
      variables: {
        investmentType,
        projectProfileId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureAssetAttributionListInvestmentSource || [];
  } catch (error) {
    throw error;
  }
}

export async function listExitsTypeForAssetAttribution(): Promise<
  Record<number, string>
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureAssetAttributionListExitTypeDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureAssetAttributionListExitType || {};
  } catch (error) {
    throw error;
  }
}

export async function listReceivedTokenForAssetAttribution(
  projectProfileId: number
): Promise<IAssetAttributionReceivedTokenModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureAssetAttributionReceivedTokenListByProjectProfileDocument,
      variables: {
        projectProfileId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.assetAttributionReceivedTokenListByProjectProfile || [];
  } catch (error) {
    throw error;
  }
}

export async function listVestingSchedulesForAssetAttribution(
  projectProfileId: number
): Promise<IAssetAttributionVestingScheduleModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureAssetAttributionVestingScheduleListByProjectProfileDocument,
      variables: {
        projectProfileId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.assetAttributionVestingScheduleListByProjectProfile || [];
  } catch (error) {
    throw error;
  }
}

export async function getAssetAttributionDescriptionsForAssetAttribution(): Promise<
  IAssetAttributionDescription[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionListAssetAttributionDescriptionsDocument,
      fetchPolicy: "cache-first",
    });

    return (
      res?.data?.ventureAssetAttributionListAssetAttributionDescriptions ?? []
    );
  } catch (e) {
    throw e;
  }
}

export async function getInvestmentRoundsForAssetAttribution(): Promise<
  IInvestmentRound[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionListInvestmentRoundsDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureAssetAttributionListInvestmentRounds ?? [];
  } catch (e) {
    throw e;
  }
}

export async function getInvestmentChecksForAssetAttribution(): Promise<
  IInvestmentCheck[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionListInvestmentChecksDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureAssetAttributionListInvestmentChecks ?? [];
  } catch (e) {
    throw e;
  }
}

export async function getCapitalInvestmentTypesForProjectProfile(): Promise<
  ICapitalInvestmentType[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionListCapitalInvestmentTypesDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureProjectProfileListCapitalInvestmentTypes ?? [];
  } catch (e) {
    throw e;
  }
}

export async function getInvestmentCurrenciesForAssetAttribution(): Promise<
  IInvestmentCurrency[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionListInvestmentCurrenciesDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureAssetAttributionListInvestmentCurrencies ?? [];
  } catch (e) {
    throw e;
  }
}

export async function getAssetAttributionExitSummaryByProjectProfile(
  projectProfileId: number
): Promise<IAssetAttributionExitSummaryModel> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionExitSummaryByProjectProfileDocument,
      variables: {
        projectProfileId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.assetAttributionExitSummaryByProjectProfile || {};
  } catch (e) {
    throw e;
  }
}

export async function getAssetAttributionCommitmentListByProjectProfile(
  projectProfileId: number
): Promise<IAssetAttributionCommitmentModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionCommitmentListByProjectProfileDocument,
      variables: {
        projectProfileId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.assetAttributionCommitmentListByProjectProfile || [];
  } catch (e) {
    throw e;
  }
}

export async function getAssetAttributionInvestmentListByProjectProfile(
  projectProfileId: number
): Promise<IAssetAttributionInvestmentModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionInvestmentListByProjectProfileDocument,
      variables: {
        projectProfileId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.assetAttributionInvestmentListByProjectProfile || [];
  } catch (e) {
    throw e;
  }
}

export async function getAssetAttributionCapitalInvestmentListByProjectProfile(
  projectProfileId: number
): Promise<IAssetAttributionCapitalInvestmentModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AssetAttributionCapitalInvestmentListByProjectProfileDocument,
      variables: {
        projectProfileId,
      },
      fetchPolicy: "network-only",
    });

    return (
      res?.data?.assetAttributionCapitalInvestmentListByProjectProfile || []
    );
  } catch (e) {
    throw e;
  }
}

export async function createAssetAttributionCommitment(
  variables: IAssetAttributionCommitmentCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionCommitmentCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionCommitmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionCommitmentCreate);
  } catch (error) {
    throw error;
  }
}

export async function updateAssetAttributionCommitment(
  variables: IAssetAttributionCommitmentUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionCommitmentUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionCommitmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionCommitmentUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deleteAssetAttributionCommitment(
  variables: IAssetAttributionCommitmentDeleteVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionCommitmentDeleteDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionCommitmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionCommitmentDelete);
  } catch (error) {
    throw error;
  }
}

export async function createAssetAttributionExit(
  variables: IAssetAttributionExitCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionExitCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionExitSummaryByProjectProfile",
        "assetAttributionExitListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionExitCreate);
  } catch (error) {
    throw error;
  }
}

export async function updateAssetAttributionExit(
  variables: IAssetAttributionExitUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionExitUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionExitSummaryByProjectProfile",
        "assetAttributionExitListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionExitUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deleteAssetAttributionExit(
  variables: IAssetAttributionExitDeleteVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionExitDeleteDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionExitSummaryByProjectProfile",
        "assetAttributionExitListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionExitDelete);
  } catch (error) {
    throw error;
  }
}

export async function createAssetAttributionReceivedToken(
  variables: IAssetAttributionReceivedTokenCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionReceivedTokenCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionReceivedTokenListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionReceivedTokenCreate);
  } catch (error) {
    throw error;
  }
}

export async function updateAssetAttributionReceivedToken(
  variables: IAssetAttributionReceivedTokenUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionReceivedTokenUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionReceivedTokenListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionReceivedTokenUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deleteAssetAttributionReceivedToken(
  id: number,
  assetAttributionId?: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionReceivedTokenDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    if (assetAttributionId) {
      executeEviction(client, {
        typename: "VentureAssetAttribution",
        id: assetAttributionId.toString(),
        fieldNames: [
          "ventureAssetAttributionGetByProjectProfileId",
          "assetAttributionReceivedTokenListByProjectProfile",
        ],
      });
    }

    return Boolean(res?.data?.ventureAssetAttributionReceivedTokenDelete);
  } catch (error) {
    throw error;
  }
}

export async function upsertAssetAttributionVestingSchedule(
  variables: IAssetAttributionVestingScheduleUpsertVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionVestingScheduleUpsertDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionVestingScheduleListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionVestingScheduleUpsert);
  } catch (error) {
    throw error;
  }
}

export async function createAssetAttributionInvestment(
  variables: IAssetAttributionInvestmentCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionInvestmentCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionInvestmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionInvestmentCreate);
  } catch (error) {
    throw error;
  }
}

export async function updateAssetAttributionInvestment(
  variables: IAssetAttributionInvestmentUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionInvestmentUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionInvestmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionInvestmentUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deleteAssetAttributionInvestment(
  variables: IAssetAttributionInvestmentDeleteVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionInvestmentDeleteDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionInvestmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionInvestmentDelete);
  } catch (error) {
    throw error;
  }
}

export async function createAssetAttributionCapitalInvestment(
  variables: IAssetAttributionCapitalInvestmentCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionCapitalInvestmentCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionCapitalInvestmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionCapitalInvestmentCreate);
  } catch (error) {
    throw error;
  }
}

export async function updateAssetAttributionCapitalInvestment(
  variables: IAssetAttributionCapitalInvestmentUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionCapitalInvestmentUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionCapitalInvestmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionCapitalInvestmentUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deleteAssetAttributionCapitalInvestment(
  variables: IAssetAttributionCapitalInvestmentDeleteVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AssetAttributionCapitalInvestmentDeleteDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureAssetAttribution",
      id: variables.assetAttributionId.toString(),
      fieldNames: [
        "ventureAssetAttributionGetByProjectProfileId",
        "assetAttributionCapitalInvestmentListByProjectProfile",
      ],
    });

    return Boolean(res?.data?.ventureAssetAttributionCapitalInvestmentDelete);
  } catch (error) {
    throw error;
  }
}
